import {
  DeleteNotificationRequestConfig,
  ListNotificationsRequestConfig,
  MarkAllNotificationsReadRequestConfig,
  MarkNotificationReadRequestConfig,
  type Notification,
  type NotificationSubscription,
  type RunningPrompt,
  RunningPromptsRequestConfig,
  subscribeToNotifications,
  UnreadCountRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { createResource, createSignal, onCleanup } from "solid-js";
import { isServer } from "solid-js/web";
import { applyDelete, applyIncoming, applyMarkAllRead, applyMarkRead } from "./notificationsMutations";

export type NotificationsService = ReturnType<typeof createNotificationsService>;

const PAGE_SIZE = 20;

export const createNotificationsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  authUserId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  const [panelEverOpened, setPanelEverOpened] = createSignal(false);
  const [nextCursor, setNextCursor] = createSignal<string | null>(null);
  const [loadingMore, setLoadingMore] = createSignal(false);
  const [activeToasts, setActiveToasts] = createSignal<Notification[]>([]);

  let subscription: NotificationSubscription | null = null;
  let sseStarted = false;

  const authGate = () => deps.authReady() && deps.authUserId() !== undefined && !isServer;

  // Eager: the badge count is the feature gate. A failed fetch (endpoint not
  // deployed) leaves the count undefined, so the badge hides and the stream
  // never opens.
  const [countData, { refetch: refetchCount, mutate: mutateCount }] = createResource(
    authGate,
    async () => {
      const res = await client.fetch(UnreadCountRequestConfig.withBearerToken(deps.authToken()), undefined, undefined);
      if (res.isErr()) return undefined;
      startStream();
      return res.value.count;
    },
    { initialValue: undefined }
  );

  // Eager like the count: the server-side snapshot of in-flight prompts is what
  // lets a fresh page load show a background generation this tab knows nothing
  // about. The client's own stream state covers the tab that launched it.
  const [runningData, { refetch: refetchRunning, mutate: mutateRunning }] = createResource(
    authGate,
    async () => {
      const res = await client.fetch(
        RunningPromptsRequestConfig.withBearerToken(deps.authToken()),
        undefined,
        undefined
      );
      if (res.isErr()) return [];
      return res.value.running;
    },
    { initialValue: [] as RunningPrompt[] }
  );

  // Lazy: the list only loads once the panel has been opened at least once.
  const [listData, { refetch: refetchList, mutate: mutateList }] = createResource(
    () => panelEverOpened() && authGate(),
    async () => {
      const res = await client.fetch(
        ListNotificationsRequestConfig.withBearerToken(deps.authToken()),
        { limit: PAGE_SIZE },
        undefined
      );
      if (res.isErr()) return [];
      setNextCursor(res.value.nextCursor);
      return res.value.notifications;
    },
    { initialValue: [] as Notification[] }
  );

  // Read .latest, not the resource getter: these run on every render and a
  // suspending read would pin the nearest Suspense on a cold fetch.
  const unreadCount = () => countData.latest ?? 0;
  const list = () => listData.latest ?? [];
  const listLoading = () => listData.loading;
  const hasMore = () => nextCursor() !== null;

  const loadMore = async () => {
    const cursor = nextCursor();
    if (!cursor || loadingMore()) return;
    setLoadingMore(true);
    const res = await client.fetch(
      ListNotificationsRequestConfig.withBearerToken(deps.authToken()),
      { cursor, limit: PAGE_SIZE },
      undefined
    );
    setLoadingMore(false);
    if (res.isErr()) return;
    setNextCursor(res.value.nextCursor);
    mutateList((prev) => [...(prev ?? []), ...res.value.notifications]);
  };

  // A failed write leaves the optimistic edit standing until a refetch pulls
  // server truth back; the count always refetches, the list only if it's loaded.
  const resyncOnError = () => {
    void refetchCount();
    if (panelEverOpened()) void refetchList();
  };

  const markRead = (id: string) => {
    const next = applyMarkRead(list(), unreadCount(), id);
    mutateList(next.list);
    mutateCount(next.count);
    void (async () => {
      const res = await client.fetch(
        MarkNotificationReadRequestConfig.withBearerToken(deps.authToken()),
        { notificationId: id },
        undefined
      );
      if (res.isErr()) resyncOnError();
    })();
  };

  const markAllRead = () => {
    const next = applyMarkAllRead(list());
    mutateList(next.list);
    mutateCount(next.count);
    void (async () => {
      const res = await client.fetch(
        MarkAllNotificationsReadRequestConfig.withBearerToken(deps.authToken()),
        undefined,
        undefined
      );
      if (res.isErr()) resyncOnError();
    })();
  };

  const remove = (id: string) => {
    const next = applyDelete(list(), unreadCount(), id);
    mutateList(next.list);
    mutateCount(next.count);
    void (async () => {
      const res = await client.fetch(
        DeleteNotificationRequestConfig.withBearerToken(deps.authToken()),
        { notificationId: id },
        undefined
      );
      if (res.isErr()) resyncOnError();
    })();
  };

  // A hidden tab can't render toasts and its list is off-screen, so buffer
  // incoming events and flush them in order when the tab returns.
  const eventQueue: Notification[] = [];
  let isTabActive = typeof document !== "undefined" ? !document.hidden : true;

  // A settled activity notification is the signal that its chat left the
  // server-side running registry: drop it locally right away and refetch to
  // heal any drift.
  const settleRunning = (n: Notification) => {
    if (n.type !== "activity.prompt.completed" && n.type !== "activity.prompt.failed") return;
    const chatId = n.payload.chatId;
    if (typeof chatId === "string") {
      mutateRunning((prev) => (prev ?? []).filter((r) => r.chatId !== chatId));
    }
    void refetchRunning();
  };

  const ingest = (n: Notification) => {
    settleRunning(n);
    if (panelEverOpened()) {
      const current = list();
      if (current.some((existing) => existing.id === n.id)) return;
      const next = applyIncoming(current, unreadCount(), n);
      mutateList(next.list);
      mutateCount(next.count);
    } else {
      mutateCount((c) => (c ?? 0) + 1);
    }
    setActiveToasts((prev) => [...prev, n]);
  };

  const handleIncoming = (n: Notification) => {
    if (!isTabActive) {
      eventQueue.push(n);
      return;
    }
    ingest(n);
  };

  const handleVisibilityChange = () => {
    if (typeof document === "undefined") return;
    const wasActive = isTabActive;
    isTabActive = !document.hidden;
    if (!wasActive && isTabActive) {
      while (eventQueue.length > 0) {
        const n = eventQueue.shift();
        if (n) ingest(n);
      }
    }
  };

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }

  const startStream = () => {
    if (sseStarted || isServer) return;
    sseStarted = true;
    subscription = subscribeToNotifications({
      authToken: deps.authToken,
      onNotification: handleIncoming,
      onReconnect: () => {
        resyncOnError();
        void refetchRunning();
      },
      onError: (error) => console.error("[NotificationsService] stream error:", error),
    });
  };

  const dismissToast = (id: string) => setActiveToasts((prev) => prev.filter((n) => n.id !== id));

  // Notifications render as a routed page rather than a popover, so the list's
  // lazy fetch is tripped by whoever mounts it.
  const ensureLoaded = () => setPanelEverOpened(true);

  onCleanup(() => {
    subscription?.close();
    subscription = null;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }
    eventQueue.length = 0;
  });

  const serverRunning = () => runningData.latest ?? [];

  return {
    unreadCount,
    serverRunning,
    list,
    listLoading,
    hasMore,
    loadMore,
    markRead,
    markAllRead,
    remove,
    activeToasts,
    dismissToast,
    ensureLoaded,
  };
};
